import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { OfficialExamResult } from '@proref/types';
import { EXAM_PASS_SCORE } from '@proref/schemas';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Skeleton } from '../../components/shared/Skeleton';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess } from '../../lib/toast';
import { downloadExamResultsCsv } from '../../services/exams.service';
import { ExamTabs, examsListPath } from '../../components/exams/ExamTabs';
import {
  useExamQuery,
  useExamResultsQuery_Admin,
  useSetExamResultsReleased,
  useDeleteExamResult,
} from '../../hooks/use-exams';

const PASS_PERCENT = EXAM_PASS_SCORE * 100;

function pct(score: number): string {
  return `${(Math.round(score * 1000) / 10).toFixed(1)}%`;
}

function hhmmss(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = h > 0 ? [h, m, s] : [m, s];
  return parts.map((n) => String(n).padStart(2, '0')).join(':');
}

// A proctoring tally. Zero renders as a plain dash rather than "0" — a table of zeroes reads as
// noise, and the point of the column is that a NON-zero draws the eye.
function FlagCell({ count, label }: { count: number; label: string }) {
  const { t } = useTranslation();
  if (count === 0) return <span className="text-muted-2">—</span>;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-warn/15 px-2 py-0.5 text-[11px] font-medium text-warn"
      title={t('admin.exams.results.flagTitle', { count, label })}
    >
      {count}
    </span>
  );
}

function ResultRow({ result, examId }: { result: OfficialExamResult; examId: string }) {
  const { t } = useTranslation();
  const del = useDeleteExamResult(examId);
  const navigate = useNavigate();
  const open = () => navigate(`/admin/exams/${examId}/results/${result.attempt_id}`);

  async function doDelete() {
    try {
      await del.mutateAsync(result.attempt_id);
      notifySuccess(t('admin.exams.results.deletedToast'));
    } catch (err) {
      notifyError(err, t('admin.exams.results.deleteErrorToast'));
    }
  }

  return (
    // The whole row opens the sitting — the same pattern as the exams list. A keyboard user gets
    // there too (tabIndex + Enter), since a clickable <tr> is invisible to tab navigation.
    <tr
      className="cursor-pointer border-t border-line transition-colors hover:bg-surface-2"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter') open();
      }}
      tabIndex={0}
      role="link"
      aria-label={t('admin.exams.results.openSittingAria', { name: result.full_name })}
    >
      <td className="px-3 py-2.5">
        <div className="text-[13px] font-medium text-ink">{result.full_name}</div>
        {result.licence_level && <div className="text-[11.5px] text-muted">{result.licence_level}</div>}
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className={'font-mono text-[13px] font-semibold ' + (result.passed ? 'text-accent' : 'text-danger')}>
          {pct(result.score)}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-[12.5px] text-ink-2">
        {result.earned_points}/{result.max_points}
      </td>
      <td className="px-3 py-2.5 text-center">
        {result.passed ? (
          <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-medium text-accent">{t('admin.exams.results.passed')}</span>
        ) : (
          <span className="rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-medium text-danger">{t('admin.exams.results.failed')}</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-[12.5px] text-muted">{hhmmss(result.duration_sec)}</td>
      <td className="px-3 py-2.5 text-center">
        <FlagCell count={result.focus_lost_count} label={t('admin.exams.results.flagLeftExam')} />
      </td>
      <td className="px-3 py-2.5 text-center">
        <FlagCell count={result.fullscreen_exit_count} label={t('admin.exams.results.flagLeftFullscreen')} />
      </td>
      <td className="px-3 py-2.5 text-center">
        <FlagCell count={result.screenshot_attempts} label={t('admin.exams.results.flagScreenshotAttempt')} />
      </td>
      {/* Stop the row's click here: the delete control lives inside a clickable row, and opening
          the sitting behind the confirm dialog would be both wrong and confusing. */}
      <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              icon
              aria-label={t('admin.exams.results.deleteResultAria', { name: result.full_name })}
            >
              <Icon.trash className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('admin.exams.results.deleteConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('admin.exams.results.deleteConfirmBody', { name: result.full_name, score: pct(result.score) })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={del.isPending}>{t('admin.exams.results.keepIt')}</AlertDialogCancel>
              <Button variant="danger" onClick={doDelete} disabled={del.isPending}>
                {del.isPending ? <Spinner className="h-4 w-4" /> : t('admin.exams.results.deleteResultButton')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}

// The federation admin's view of one exam's cohort: who sat it, what they scored, how long they
// took, and the advisory proctoring flags — plus the switch that decides whether candidates can
// see the per-question breakdown yet.
export function ExamResultsAdminPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const examQuery = useExamQuery(id);
  const resultsQuery = useExamResultsQuery_Admin(id);
  const release = useSetExamResultsReleased(id ?? '');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const exam = examQuery.data;
  const released = Boolean(exam?.results_released_at);

  const results = useMemo(() => {
    const rows = resultsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => r.full_name.toLowerCase().includes(q)) : rows;
  }, [resultsQuery.data, search]);

  // Cohort summary across ALL results, not the filtered view — a search box is for finding one
  // candidate, and having the averages jump around while you type would be actively misleading.
  const summary = useMemo(() => {
    const rows = resultsQuery.data ?? [];
    if (rows.length === 0) return null;
    return {
      total: rows.length,
      passed: rows.filter((r) => r.passed).length,
      average: rows.reduce((sum, r) => sum + r.score, 0) / rows.length,
      flagged: rows.filter(
        (r) => r.focus_lost_count > 0 || r.screenshot_attempts > 0 || r.fullscreen_exit_count > 0,
      ).length,
    };
  }, [resultsQuery.data]);

  async function toggleRelease() {
    try {
      await release.mutateAsync(!released);
      notifySuccess(released ? t('admin.exams.results.withheldToast') : t('admin.exams.results.releasedToast'));
    } catch (err) {
      notifyError(err, t('admin.exams.results.releaseErrorToast'));
    }
  }

  async function doExport() {
    setExporting(true);
    try {
      await downloadExamResultsCsv(id as string, `${exam?.title ?? 'exam'}-results.csv`);
      notifySuccess(t('admin.exams.results.exportedToast'));
    } catch (err) {
      notifyError(err, t('admin.exams.results.exportErrorToast'));
    } finally {
      setExporting(false);
    }
  }

  if (examQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (examQuery.isError || !exam) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(examQuery.error, t('admin.exams.results.couldNotLoadExam'))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2">
        <Link
          to={examsListPath(exam.type)}
          className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink"
        >
          <Icon.chevronLeft className="h-3.5 w-3.5" /> {t('admin.exams.results.allExams')}
        </Link>
      </div>

      {/* Header mirrors the Questions tab: the EXAM is the subject on both, so the title stays the
          exam's own and the tab bar says which half you are looking at. */}
      <PageHeader
        title={exam.title}
        sub={t('admin.exams.results.sub')}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={doExport} disabled={exporting || (resultsQuery.data ?? []).length === 0}>
              {exporting ? <Spinner className="h-4 w-4" /> : <Icon.doc className="h-3.5 w-3.5" />} {t('admin.exams.results.exportCsv')}
            </Button>
            <Button variant={released ? 'ghost' : 'accent'} onClick={toggleRelease} disabled={release.isPending}>
              {release.isPending ? (
                <Spinner className="h-4 w-4" />
              ) : released ? (
                t('admin.exams.results.withholdDetails')
              ) : (
                t('admin.exams.results.releaseDetails')
              )}
            </Button>
          </div>
        }
      />

      <ExamTabs examId={exam.id} />

      {/* Release state. Only meaningful on a proctored exam — an ordinary practice test reveals
          its answers as soon as it's submitted and has nothing to withhold. */}
      {exam.is_proctored ? (
        <div
          className={
            'mb-5 flex items-start gap-3 rounded-lg border p-4 ' +
            (released ? 'border-accent/40 bg-accent/[0.06]' : 'border-warn/40 bg-warn/[0.07]')
          }
        >
          <div className={'grid h-7 w-7 flex-none place-items-center rounded-full ' + (released ? 'bg-accent/15 text-accent' : 'bg-warn/20 text-warn')}>
            <Icon.lock className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-ink">
              {released ? t('admin.exams.results.answersReleased') : t('admin.exams.results.answersWithheld')}
            </div>
            <p className="mt-0.5 text-[12.5px] text-muted">
              {released ? t('admin.exams.results.releasedNote') : t('admin.exams.results.withheldNote')}
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-5 rounded-lg border border-line bg-surface p-4 text-[12.5px] text-muted">
          {t('admin.exams.results.practiceNote')}
        </div>
      )}

      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: t('admin.exams.results.statSittings'), value: String(summary.total), tone: 'text-ink' },
            {
              label: t('admin.exams.results.statPassed', { percent: PASS_PERCENT }),
              value: `${summary.passed}/${summary.total}`,
              tone: 'text-accent',
            },
            { label: t('admin.exams.results.statAverage'), value: pct(summary.average), tone: 'text-ink' },
            {
              label: t('admin.exams.results.statFlagged'),
              value: String(summary.flagged),
              tone: summary.flagged > 0 ? 'text-warn' : 'text-ink-2',
            },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border border-line bg-surface p-4">
              <div className={'font-display text-[24px] leading-none ' + stat.tone}>{stat.value}</div>
              <div className="mt-1.5 text-[11.5px] text-muted">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 max-w-xs">
        <Input
          placeholder={t('admin.exams.results.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('admin.exams.results.searchAria')}
        />
      </div>

      {resultsQuery.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : resultsQuery.isError ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(resultsQuery.error, t('admin.exams.results.couldNotLoadResults'))}
        </div>
      ) : (resultsQuery.data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
          <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-surface-2 text-muted">
            <Icon.trophy className="h-4 w-4" />
          </div>
          <div className="text-[13.5px] font-medium text-ink">{t('admin.exams.results.nobodySatYet')}</div>
          <p className="mt-1 text-[12.5px] text-muted">{t('admin.exams.results.resultsAppearHere')}</p>
        </div>
      ) : (
        // The table scrolls inside its own container rather than pushing the page sideways.
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full min-w-[820px] border-collapse text-left">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.06em] text-muted">
                <th className="px-3 py-2.5 font-medium">{t('admin.exams.results.colCandidate')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('admin.exams.results.colScore')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('admin.exams.results.colPoints')}</th>
                <th className="px-3 py-2.5 text-center font-medium">{t('admin.exams.results.colResult')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('admin.exams.results.colDuration')}</th>
                <th className="px-3 py-2.5 text-center font-medium">{t('admin.exams.results.colLeftExam')}</th>
                <th className="px-3 py-2.5 text-center font-medium">{t('admin.exams.results.colLeftFullscreen')}</th>
                <th className="px-3 py-2.5 text-center font-medium">{t('admin.exams.results.colScreenshots')}</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <ResultRow key={r.attempt_id} result={r} examId={id as string} />
              ))}
            </tbody>
          </table>
          {results.length === 0 && (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted">
              {t('admin.exams.results.noCandidateMatch', { search })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ExamResultsAdminPage;
